/**
 * BlockNote Diff Inline Plugin
 *
 * ProseMirror plugin for visualizing inline diffs on hover in BlockNote editors
 */

import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const diffPluginKey = new PluginKey('diff-inline');

/**
 * Diff operation tuple from backend: [operation, text]
 * operation: -1 = delete, 0 = unchanged, 1 = insert
 */
export type DiffOperation = [number, string];

/**
 * Change type for a line
 */
type ChangeType = 'insert' | 'delete' | 'modify';

/**
 * Line position information
 */
interface LinePosition {
  start: number;
  end: number;
  line: number;
}

/**
 * Get line number at a given text position
 */
function getLineAtPosition(pos: number, linePositions: LinePosition[]): number {
  for (const linePos of linePositions) {
    if (pos >= linePos.start && pos <= linePos.end) {
      return linePos.line;
    }
  }
  return linePositions.length > 0 ? linePositions[linePositions.length - 1].line : 0;
}

/**
 * Get the document position at the start of a line
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getLinePosition(lineNumber: number, doc: any): number | null {
  let currentLine = 0;
  let pos = 0;

  // Walk through the document to find the line start position
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  doc.descendants((node: any, nodePos: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (node.isBlock) {
      if (currentLine === lineNumber) {
        pos = nodePos;
        return false; // Stop traversal
      }
      currentLine++;
    }
    return true;
  });

  return currentLine === lineNumber ? pos : null;
}

/**
 * Map diffs to line numbers
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDiffsToLines(diffs: DiffOperation[], doc: any): Map<number, ChangeType> {
  const lineChanges = new Map<number, ChangeType>();
  let textPos = 0;

  // Build line position map
  const linePositions: LinePosition[] = [];
  let currentLine = 0;
  let lineStart = 0;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const fullText = doc.textContent;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  for (let i = 0; i <= fullText.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (i === fullText.length || fullText[i] === '\n') {
      linePositions.push({ start: lineStart, end: i, line: currentLine });
      lineStart = i + 1;
      currentLine++;
    }
  }

  // Process diffs
  diffs.forEach(([operation, text]) => {
    const length = text.length;
    const startLine = getLineAtPosition(textPos, linePositions);
     
    const endLine = getLineAtPosition(
      Math.min(textPos + length - 1, fullText.length - 1),
      linePositions,
    );

    for (let line = startLine; line <= endLine; line++) {
      const existingChange = lineChanges.get(line);

      if (operation === 1) {
        // Insert
        if (existingChange === 'delete') {
          lineChanges.set(line, 'modify');
        } else if (!existingChange) {
          lineChanges.set(line, 'insert');
        }
      } else if (operation === -1) {
        // Delete
        if (existingChange === 'insert') {
          lineChanges.set(line, 'modify');
        } else if (!existingChange) {
          lineChanges.set(line, 'delete');
        }
      }
    }

    if (operation !== -1) {
      textPos += length;
    }
  });

  return lineChanges;
}

/**
 * Create inline diff decorations for a specific line (shown on hover)
 */
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-argument, unused-imports/no-unused-vars */
function createInlineDiffDecorations(
  lineNumber: number,
  diffs: DiffOperation[],
  doc: any,
): Decoration[] {
  const decorations: Decoration[] = [];

  // Get the position range for this line
  const linePos = getLinePosition(lineNumber, doc);
  if (linePos === null) return decorations;

  // Find the block at this position
  let blockStart = linePos;
  let blockEnd = linePos;
  let blockText = '';

  doc.descendants((node: any, pos: number) => {
    if (node.isBlock && pos === linePos) {
      blockStart = pos;
      blockEnd = pos + node.nodeSize;
      blockText = node.textContent || '';
      return false; // Stop traversal
    }
    return true;
  });

  // Build a text position to diff operation map
  let textPos = 0;
  const fullText = doc.textContent;

  diffs.forEach(([operation, text]) => {
    const length = text.length;
    const start = textPos;
    const end = textPos + length;

    // Check if this diff operation overlaps with the current block
    const docTextUpToBlock = fullText.substring(0, blockStart);
    const blockTextStart = docTextUpToBlock.length;
    const blockTextEnd = blockTextStart + blockText.length;

    if (operation !== 0 && start < blockTextEnd && end > blockTextStart) {
      // This operation affects this block
      const overlapStart = Math.max(start, blockTextStart);
      const overlapEnd = Math.min(end, blockTextEnd);

      if (operation === 1) {
        // Addition: green background with underline
        const fromPos = blockStart + (overlapStart - blockTextStart);
        const toPos = blockStart + (overlapEnd - blockTextStart);

        decorations.push(
          Decoration.inline(fromPos, toPos, {
            class: 'diff-addition-inline',
          }),
        );
      } else if (operation === -1) {
        // Deletion: show as widget (cached text to avoid repeated substring operations)
        const insertPos = blockStart + (overlapStart - blockTextStart);
        const deletedText = text.substring(overlapStart - start, overlapEnd - start);
        decorations.push(
          Decoration.widget(insertPos, () => {
            const span = document.createElement('span');
            span.className = 'diff-deletion-inline';
            span.textContent = deletedText;
            return span;
          }),
        );
      }
    }

    if (operation !== -1) {
      textPos += length;
    }
  });

  return decorations;
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-unsafe-argument, unused-imports/no-unused-vars */

/**
 * Creates a ProseMirror plugin that shows inline diff decorations on hover
 *
 * @param diff - Array of diff operations from backend
 * @returns ProseMirror plugin
 */
export function createDiffPlugin(diff: DiffOperation[]) {
  if (!diff || diff.length === 0) {
    // Return a minimal plugin if there are no diffs
    return new Plugin({
      key: diffPluginKey,
      state: {
        init() {
          return { decorations: DecorationSet.empty, lineChanges: new Map() };
        },
        apply(tr, state) {
          // Map decorations through document changes
          return {
            decorations: state.decorations.map(tr.mapping, tr.doc),
            lineChanges: state.lineChanges,
          };
        },
      },
      props: {
        decorations(state) {
          return this.getState(state)?.decorations || DecorationSet.empty;
        },
      },
    });
  }

  return new Plugin({
    key: diffPluginKey,
    state: {
      init(_, { doc }) {
        const lineChanges = mapDiffsToLines(diff, doc);
        return {
          inlineDecorations: DecorationSet.empty,
          lineChanges,
          hoveredLine: null,
          diffs: diff,
        };
      },
      apply(tr, state) {
        // Check if there's a new diff to apply
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const applyDiffs = tr.getMeta('applyDiffs');
        if (applyDiffs) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const lineChanges = mapDiffsToLines(applyDiffs, tr.doc);
          return {
            inlineDecorations: DecorationSet.empty,
            lineChanges,
            hoveredLine: null,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            diffs: applyDiffs,
          };
        }

        // Check if hover state changed
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const setHoveredLine = tr.getMeta('setHoveredLine');
        if (setHoveredLine !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const hoveredLine = setHoveredLine;
          let inlineDecorations = DecorationSet.empty;

          if (hoveredLine !== null && state.lineChanges.has(hoveredLine)) {
            // Create inline decorations for the hovered line
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const inlineDecos = createInlineDiffDecorations(hoveredLine, state.diffs, tr.doc);

            inlineDecorations = DecorationSet.create(tr.doc, inlineDecos);
          }

          return {
            inlineDecorations,
            lineChanges: state.lineChanges,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            hoveredLine,
            diffs: state.diffs,
          };
        }

        // Map decorations through document changes
        if (tr.docChanged) {
          // Recreate decorations on document changes
          const lineChanges = state.lineChanges;

          let inlineDecorations = DecorationSet.empty;
          if (state.hoveredLine !== null && lineChanges.has(state.hoveredLine)) {
            const inlineDecos = createInlineDiffDecorations(state.hoveredLine, state.diffs, tr.doc);

            inlineDecorations = DecorationSet.create(tr.doc, inlineDecos);
          }

          return {
            inlineDecorations,
            lineChanges,
            hoveredLine: state.hoveredLine,
            diffs: state.diffs,
          };
        }

        return {
          inlineDecorations: state.inlineDecorations.map(tr.mapping, tr.doc),
          lineChanges: state.lineChanges,
          hoveredLine: state.hoveredLine,
          diffs: state.diffs,
        };
      },
    },
    props: {
      decorations(state) {
        const pluginState = this.getState(state);
        if (!pluginState) return DecorationSet.empty;

        // Return only inline decorations (gutter is handled by React component)
        return pluginState.inlineDecorations;
      },
      handleDOMEvents: {
        mouseover(view, event) {
          // Use ProseMirror's posAtCoords to directly map mouse position to document position
          const coords = { left: event.clientX, top: event.clientY };
          const posResult = view.posAtCoords(coords);

          if (!posResult) {
            const tr = view.state.tr.setMeta('setHoveredLine', null);
            view.dispatch(tr);
            return false;
          }

          // Find which line this position corresponds to
          let currentLine = 0;
          let found = false;
          const targetPos = posResult.pos;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          view.state.doc.descendants((node: any, nodePos: number) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (node.isBlock) {
              // Check if target position is within this block
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-plus-operands
              if (targetPos >= nodePos && targetPos < nodePos + node.nodeSize) {
                found = true;
                return false; // Stop traversal
              }
              // If we've passed the target position, stop searching
              if (targetPos < nodePos) {
                return false;
              }
              currentLine++;
            }
            return true;
          });

          if (found) {
            const pluginState = diffPluginKey.getState(view.state);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (pluginState?.lineChanges.has(currentLine)) {
              // Only update if hovering a changed line
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (pluginState.hoveredLine !== currentLine) {
                const tr = view.state.tr.setMeta('setHoveredLine', currentLine);
                view.dispatch(tr);
              }
            } else {
              // Hovering a non-changed line, clear hover
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (pluginState?.hoveredLine !== null) {
                const tr = view.state.tr.setMeta('setHoveredLine', null);
                view.dispatch(tr);
              }
            }
          } else {
            // Position not in any block, clear hover
            const tr = view.state.tr.setMeta('setHoveredLine', null);
            view.dispatch(tr);
          }

          return false;
        },
        mouseout(view, event) {
          const relatedTarget = event.relatedTarget as HTMLElement;

          // Check if we're leaving the editor entirely
          if (!relatedTarget || !view.dom.contains(relatedTarget)) {
            const tr = view.state.tr.setMeta('setHoveredLine', null);
            view.dispatch(tr);
          }

          return false;
        },
      },
    },
  });
}
